/**
 * useSipaSignalR.js
 * 
 * Custom React hook for real-time SIPA Report collaboration via SignalR.
 * 
 * This hook establishes a persistent WebSocket connection to the server's SignalR hub
 * and provides real-time updates for multi-user SIPA form editing. It automatically:
 * - Manages connection lifecycle (connect on mount, disconnect on unmount)
 * - Handles reconnection with exponential backoff
 * - Joins/leaves report-specific groups for targeted notifications
 * - Provides user presence tracking (who's viewing/editing)
 * - Enables field-level editing awareness (which user is editing which field)
 * - Broadcasts answer updates in real-time
 * - Notifies when reports are signed off
 * 
 * Usage:
 * ```jsx
 * const { 
 *   isConnected, 
 *   activeUsers, 
 *   fieldEditingStatus,
 *   joinReport,
 *   leaveReport,
 *   notifyFieldEditStart,
 *   notifyFieldEditEnd
 * } = useSipaSignalR({
 *   reportId: '123',
 *   onAnswersUpdated: (update) => { console.log('Answers updated:', update); },
 *   onReportSignedOff: (update) => { console.log('Report signed off:', update); },
 *   enabled: true, // Set to false to disable the connection
 * });
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { HubConnectionBuilder, LogLevel, HubConnectionState } from '@microsoft/signalr';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';

/**
 * Configuration options for the SignalR hook
 */
const DEFAULT_OPTIONS = {
    /** Enable or disable the SignalR connection */
    enabled: true,
    /** Log level for SignalR (None, Error, Warning, Information, Debug, Trace) */
    logLevel: LogLevel.Warning,
    /** Automatic reconnect delays in milliseconds */
    reconnectDelays: [0, 2000, 5000, 10000, 30000],
};

/**
 * Custom hook for real-time SIPA Report collaboration via SignalR
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.reportId - The SIPA report ID to subscribe to
 * @param {Function} options.onAnswersUpdated - Callback when answers are updated by another user
 * @param {Function} options.onReportSignedOff - Callback when the report is signed off
 * @param {Function} options.onUserJoined - Callback when another user joins the report
 * @param {Function} options.onUserLeft - Callback when another user leaves the report
 * @param {Function} options.onFieldEditStarted - Callback when another user starts editing a field
 * @param {Function} options.onFieldEditEnded - Callback when another user stops editing a field
 * @param {boolean} options.enabled - Whether the connection should be active
 * @returns {Object} Hook state and methods
 */
const useSipaSignalR = (options = {}) => {
    const { user } = useAuth();
    const connectionRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [activeUsers, setActiveUsers] = useState([]); // List of users currently viewing the report
    const [fieldEditingStatus, setFieldEditingStatus] = useState({}); // Map of controlId -> { userId, userName }

    const {
        reportId,
        onAnswersUpdated,
        onReportSignedOff,
        onUserJoined,
        onUserLeft,
        onFieldEditStarted,
        onFieldEditEnded,
        enabled = DEFAULT_OPTIONS.enabled,
    } = options;

    /**
     * Gets the auth token for SignalR connection
     */
    const getAccessToken = useCallback(() => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            return storedUser?.token || '';
        } catch (catchError) {
            Sentry.captureException(catchError);
            return '';
        }
    }, []);

    /**
     * Joins a report group to receive report-specific updates
     */
    const joinReport = useCallback(async (repId) => {
        const targetReportId = repId || reportId;
        if (connectionRef.current?.state === HubConnectionState.Connected && targetReportId) {
            try {
                await connectionRef.current.invoke('JoinReportGroup', targetReportId);
                console.debug(`Joined SIPA report group: ${targetReportId}`);
            } catch (err) {
                Sentry.captureException(err);
                console.error('Failed to join report group:', err);
            }
        }
    }, [reportId]);

    /**
     * Leaves a report group
     */
    const leaveReport = useCallback(async (repId) => {
        const targetReportId = repId || reportId;
        if (connectionRef.current?.state === HubConnectionState.Connected && targetReportId) {
            try {
                await connectionRef.current.invoke('LeaveReportGroup', targetReportId);
                console.debug(`Left SIPA report group: ${targetReportId}`);
            } catch (err) {
                Sentry.captureException(err);
                console.error('Failed to leave report group:', err);
            }
        }
    }, [reportId]);

    /**
     * Notifies other users that this user started editing a field
     */
    const notifyFieldEditStart = useCallback(async (controlId, subSectionId) => {
        if (connectionRef.current?.state === HubConnectionState.Connected && reportId) {
            try {
                await connectionRef.current.invoke('NotifyFieldEditStart', reportId, controlId, subSectionId);
            } catch (err) {
                Sentry.captureException(err);
                console.error('Failed to notify field edit start:', err);
            }
        }
    }, [reportId]);

    /**
     * Notifies other users that this user stopped editing a field
     */
    const notifyFieldEditEnd = useCallback(async (controlId) => {
        if (connectionRef.current?.state === HubConnectionState.Connected && reportId) {
            try {
                await connectionRef.current.invoke('NotifyFieldEditEnd', reportId, controlId);
            } catch (err) {
                Sentry.captureException(err);
                console.error('Failed to notify field edit end:', err);
            }
        }
    }, [reportId]);

    /**
     * Sends a ping to verify connection is alive
     * @returns {Promise<Date>} Server timestamp
     */
    const ping = useCallback(async () => {
        if (connectionRef.current?.state === HubConnectionState.Connected) {
            try {
                return await connectionRef.current.invoke('Ping');
            } catch (err) {
                Sentry.captureException(err);
                console.error('Ping failed:', err);
                return null;
            }
        }
        return null;
    }, []);

    // Store callbacks in refs to avoid re-creating connection when they change
    const callbacksRef = useRef({
        onAnswersUpdated,
        onReportSignedOff,
        onUserJoined,
        onUserLeft,
        onFieldEditStarted,
        onFieldEditEnded,
    });

    // Update refs when callbacks change
    useEffect(() => {
        callbacksRef.current = {
            onAnswersUpdated,
            onReportSignedOff,
            onUserJoined,
            onUserLeft,
            onFieldEditStarted,
            onFieldEditEnded,
        };
    }, [onAnswersUpdated, onReportSignedOff, onUserJoined, onUserLeft, onFieldEditStarted, onFieldEditEnded]);

    // Initialize and manage SignalR connection
    useEffect(() => {
        // Don't connect if disabled or no user or no reportId
        if (!enabled || !user?.userID || !reportId) {
            return;
        }

        // Prevent multiple simultaneous connections
        if (connectionRef.current) {
            console.debug('[SignalR SIPA] Connection already exists, skipping creation');
            return;
        }

        console.debug('[SignalR SIPA] Creating new connection for report:', reportId);

        // Build the connection.
        // Hub path is /api/hubs/sipareports so that the production Nginx /api/ location
        // block (which carries the WebSocket upgrade headers) correctly forwards the
        // request to the backend.  /hubs/ at the root resolves to the static-file server
        // which returns 404.
        const connection = new HubConnectionBuilder()
            .withUrl('/api/hubs/sipareports', {
                accessTokenFactory: getAccessToken,
            })
            .withAutomaticReconnect(DEFAULT_OPTIONS.reconnectDelays)
            .configureLogging(DEFAULT_OPTIONS.logLevel)
            .build();

        connectionRef.current = connection;

        // Set up event handlers for real-time collaboration

        // Handle answer updates from other users
        connection.on('AnswersUpdated', (update) => {
            setLastUpdate({ type: 'AnswersUpdated', data: update, timestamp: new Date() });
            callbacksRef.current.onAnswersUpdated?.(update);
        });

        // Handle report sign-off events
        connection.on('ReportSignedOff', (update) => {
            setLastUpdate({ type: 'ReportSignedOff', data: update, timestamp: new Date() });
            callbacksRef.current.onReportSignedOff?.(update);
        });

        // Handle user presence: when another user joins the report
        connection.on('UserJoinedReport', (update) => {
            // UserJoinedReport event received (user info not logged to prevent cleartext logging)
            
            setActiveUsers(prev => {
                // Handle both PascalCase and camelCase property names
                const userId = update.UserId || update.userId;
                const userName = update.UserName || update.userName;
                const userEmail = update.UserEmail || update.userEmail;
                
                // User info extracted (removed to prevent cleartext logging)
                
                // Avoid duplicates
                const exists = prev.some(u => (u.UserId || u.userId) === userId);
                if (exists) {
                    console.log('[SignalR SIPA Hook] User already in activeUsers list');
                    return prev;
                }
                
                const newUser = { 
                    UserId: userId, 
                    userId: userId,
                    UserName: userName, 
                    userName: userName,
                    UserEmail: userEmail,
                    userEmail: userEmail
                };
                
                // User added to activeUsers list (removed to prevent cleartext logging)
                return [...prev, newUser];
            });
            setLastUpdate({ type: 'UserJoined', data: update, timestamp: new Date() });
            callbacksRef.current.onUserJoined?.(update);
        });

        // Handle user presence: when another user leaves the report
        connection.on('UserLeftReport', (update) => {
            // UserLeftReport event received (user info not logged to prevent cleartext logging)
            
            const userId = update.UserId || update.userId;
            setActiveUsers(prev => prev.filter(u => (u.UserId || u.userId) !== userId));
            
            // Clear any field editing status for this user
            setFieldEditingStatus(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(controlId => {
                    if (next[controlId]?.userId === userId) {
                        delete next[controlId];
                    }
                });
                return next;
            });
            
            setLastUpdate({ type: 'UserLeft', data: update, timestamp: new Date() });
            callbacksRef.current.onUserLeft?.(update);
        });

        // Handle field-level editing indicators: when another user starts editing a field
        connection.on('FieldEditStarted', (update) => {
            setFieldEditingStatus(prev => ({
                ...prev,
                [update.ControlId]: {
                    userId: update.UserId,
                    userName: update.UserName,
                    timestamp: new Date(update.Timestamp)
                }
            }));
            setLastUpdate({ type: 'FieldEditStarted', data: update, timestamp: new Date() });
            callbacksRef.current.onFieldEditStarted?.(update);
        });

        // Handle field-level editing indicators: when another user stops editing a field
        connection.on('FieldEditEnded', (update) => {
            setFieldEditingStatus(prev => {
                const next = { ...prev };
                delete next[update.ControlId];
                return next;
            });
            setLastUpdate({ type: 'FieldEditEnded', data: update, timestamp: new Date() });
            callbacksRef.current.onFieldEditEnded?.(update);
        });

        connection.on('Pong', (serverTime) => {
            console.debug('Pong received:', serverTime);
        });

        // Handle initial active users list when joining a report
        connection.on('InitialActiveUsers', (data) => {
            // InitialActiveUsers event received (removed to prevent cleartext logging)
            
            if (data?.ActiveUsers && Array.isArray(data.ActiveUsers)) {
                console.log(`[SignalR SIPA Hook] Setting initial active users: ${data.ActiveUsers.length} users (details removed to prevent cleartext logging)`);
                setActiveUsers(data.ActiveUsers);
            } else {
                console.log('[SignalR SIPA Hook] No active users in data');
            }
        });

        // Connection state handlers
        connection.onclose((error) => {
            setIsConnected(false);
            if (error) {
                console.error('SignalR connection closed with error:', error);
                setConnectionError(error.message);
            }
            // Clear active users and field editing status on disconnect
            setActiveUsers([]);
            setFieldEditingStatus({});
        });

        connection.onreconnecting((error) => {
            setIsConnected(false);
            console.debug('SignalR reconnecting...', error);
        });

        connection.onreconnected(async (connectionId) => {
            setIsConnected(true);
            setConnectionError(null);
            console.debug('SignalR reconnected with connectionId:', connectionId);
            
            // Rejoin the report group after reconnection and get active users
            if (reportId) {
                await joinReport(reportId);
            }
        });

        // Start the connection
        const startConnection = async () => {
            try {
                await connection.start();
                setIsConnected(true);
                setConnectionError(null);
                console.debug('SignalR connected successfully');
                
                // Automatically join the report group if reportId is provided
                if (reportId) {
                    await joinReport(reportId);
                }
            } catch (err) {
                Sentry.captureException(err);
                console.error('SignalR connection failed:', err);
                setConnectionError(err.message);
                setIsConnected(false);
            }
        };

        startConnection();

        // Cleanup on unmount or when dependencies change
        return () => {
            if (connection && reportId) {
                // Leave the report group before disconnecting
                connection.invoke('LeaveReport', reportId).catch(() => {});
            }
            if (connection) {
                connection.stop().catch(err => console.debug('Error stopping SignalR:', err));
            }
            // Clear the ref so a new connection can be created
            connectionRef.current = null;
        };
    }, [
        enabled,
        user?.userID,
        reportId,
        getAccessToken,
        joinReport,
    ]);

    return {
        /** Whether the SignalR connection is currently active */
        isConnected,
        /** Any connection error message */
        connectionError,
        /** The last update received from SignalR */
        lastUpdate,
        /** List of users currently viewing/editing the report */
        activeUsers,
        /** Map of field IDs to editing user info (controlId -> { userId, userName, timestamp }) */
        fieldEditingStatus,
        /** Join a report group for report-specific updates */
        joinReport,
        /** Leave a report group */
        leaveReport,
        /** Notify other users that this user started editing a field */
        notifyFieldEditStart,
        /** Notify other users that this user stopped editing a field */
        notifyFieldEditEnd,
        /** Send a ping to verify connection */
        ping,
    };
};

export default useSipaSignalR;
